import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  Chip,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Checkbox,
  Collapse,
  Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import { getProvinciaFromCodigo, getComunidadFromCodigoProvincia } from '../zoneData.js';

/**
 * Función de utilidad para procesar clientes de Freematica
 * @param {Array} clientesData - Array de clientes sin procesar
 * @returns {Array} - Array de clientes procesados con provincia y comunidad
 */
export const procesarClientesFreematica = (clientesData) => {
  return clientesData.map(cliente => {
    // Asegurar que el nombre esté presente
    const nombre = cliente.nombre || cliente.nombreCliente || `Cliente ${cliente.id}`;
    
    // Si el cliente tiene estructura de Freematica con direccion
    if (cliente.direccion && typeof cliente.direccion === 'object') {
      const direccion = cliente.direccion;
      const codigoProvincia = direccion.provincia;
      const nombreProvincia = getProvinciaFromCodigo(codigoProvincia);
      const comunidad = getComunidadFromCodigoProvincia(codigoProvincia);
      
      return {
        ...cliente,
        nombre: nombre,
        provincia: nombreProvincia,
        comunidad: comunidad || 'Sin comunidad',
        // Mantener la información original de dirección
        direccionOriginal: direccion
      };
    }
    // Si el cliente tiene provincia directa (estructura antigua)
    else if (cliente.provincia && typeof cliente.provincia === 'string') {
      const nombreProvincia = getProvinciaFromCodigo(cliente.provincia);
      const comunidad = getComunidadFromCodigoProvincia(cliente.provincia);
      
      return {
        ...cliente,
        nombre: nombre,
        provincia: nombreProvincia,
        comunidad: comunidad || 'Sin comunidad'
      };
    }
    // Si no tiene estructura de Freematica, mantener como está pero asegurar el nombre
    return {
      ...cliente,
      nombre: nombre
    };
  });
};

/**
 * Componente de selección de clientes con búsqueda optimizada y lazy loading
 * @param {Object} props - Propiedades del componente
 * @param {Array} props.clientes - Lista de clientes disponibles
 * @param {Array} props.selectedClientes - Lista de IDs de clientes seleccionados
 * @param {Function} props.onChange - Función a llamar cuando cambia la selección
 * @param {boolean} props.disabled - Indica si el componente está deshabilitado
 * @param {boolean} props.required - Indica si el campo es obligatorio
 * @param {boolean} props.loading - Indica si está cargando datos
 * @param {string} props.searchTerm - Término de búsqueda actual
 * @param {Function} props.setSearchTerm - Función para establecer el término de búsqueda
 * @param {boolean} props.hasMore - Indica si hay más datos para cargar
 * @param {boolean} props.isLoadingMore - Indica si está cargando más datos
 * @param {React.RefObject} props.observerRef - Referencia para el observer de scroll
 * @param {Function} props.loadMoreClientes - Función para cargar más clientes manualmente
 * @returns {JSX.Element} - Componente ClienteSearch
 */
const ClienteSearch = ({
  clientes = [],
  selectedClientes = [],
  onChange,
  disabled = false,
  required = false,
  loading = false,
  searchTerm: searchTermProp = '',
  setSearchTerm: setSearchTermProp,
  hasMore = false,
  isLoadingMore = false,
  observerRef,
  loadMoreClientes,
  hideLabel = false
}) => {
  const [open, setOpen] = useState(false);
  const [internalSearchTerm, setInternalSearchTerm] = useState('');

  // Usar estado interno cuando setSearchTerm no se pasa (ej. ClientPortalConfig)
  const searchTerm = typeof setSearchTermProp === 'function' ? searchTermProp : internalSearchTerm;
  const setSearchTerm = typeof setSearchTermProp === 'function' ? setSearchTermProp : setInternalSearchTerm;

  // Asegurar que selectedClientes sea siempre un array
  const selectedArray = useMemo(() => {
    return Array.isArray(selectedClientes) ? selectedClientes : [];
  }, [selectedClientes]);

  // Filtrar clientes basados en el texto de búsqueda (con deduplicación)
  const filteredClientes = useMemo(() => {
    if (!clientes || clientes.length === 0) return [];
    
    // Primero filtrar por término de búsqueda
    const filtered = clientes.filter(cliente => {
      const searchLower = searchTerm.toLowerCase();
      const nombre = cliente.nombre || cliente.nombreCliente || cliente.id || 'Cliente sin nombre';
      return nombre.toLowerCase().includes(searchLower);
    });
    
    // Eliminar duplicados por ID
    const seen = new Set();
    return filtered.filter(cliente => {
      if (seen.has(cliente.id)) {
        return false;
      }
      seen.add(cliente.id);
      return true;
    });
  }, [clientes, searchTerm]);

  // Renderizar la etiqueta del cliente
  const renderClienteLabel = useCallback((cliente) => {
    const nombre = cliente.nombre || cliente.nombreCliente || cliente.id || 'Cliente sin nombre';
    if (cliente.provincia) {
      return `${nombre} (${cliente.provincia})`;
    }
    return nombre;
  }, []);

  // Manejar cambios en la selección de un cliente individual
  const handleClienteToggle = useCallback((clienteId, isChecked) => {
    console.log('🔍 ClienteSearch handleClienteToggle:', { clienteId, isChecked, selectedArray });
    
    const newSelectedClientes = isChecked 
      ? [...selectedArray, clienteId]
      : selectedArray.filter(id => id !== clienteId);
    
    console.log('🔍 Nuevos clientes seleccionados:', newSelectedClientes);
    onChange(newSelectedClientes);
  }, [selectedArray, onChange]);

  // Manejar cambios en la búsqueda
  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
  }, [setSearchTerm]);

  // Limpiar búsqueda
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
  }, [setSearchTerm]);

  // Loading state
  if (loading) {
    return (
      <FormControl fullWidth disabled={disabled} required={required}>
        <InputLabel>Cargando clientes...</InputLabel>
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <CircularProgress size={20} />
          <Typography variant="body2" sx={{ mt: 1 }}>
            Cargando clientes...
          </Typography>
        </Box>
      </FormControl>
    );
  }

  // Estado cuando no hay clientes cargados (diferente de cuando no hay resultados de búsqueda)
  if (clientes.length === 0 && !loading && !searchTerm) {
    return (
      <FormControl fullWidth disabled={disabled} required={required}>
        <InputLabel>Clientes</InputLabel>
        <Box sx={{ p: 2 }}>
          <Alert severity="info">
            No hay clientes disponibles. Verifique que tiene clientes asignados.
          </Alert>
        </Box>
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth disabled={disabled} required={required}>
      {!hideLabel && <InputLabel>Clientes</InputLabel>}
      
      {/* Botón para expandir/colapsar */}
      <Button
        variant="contained"
        onClick={() => setOpen(!open)}
        endIcon={open ? <ExpandLess /> : <ExpandMore />}
        fullWidth
        sx={{ 
          justifyContent: 'space-between', 
          textTransform: 'none',
          backgroundColor: 'rgb(70, 120, 195)',
          color: '#fff',
          marginTop: hideLabel ? 0 : '8px',
          '&:hover': {
            backgroundColor: 'rgb(55, 100, 170)'
          }
        }}
        disabled={disabled}
      >
        <span>{hideLabel ? (selectedArray.length > 0 ? 'Cliente seleccionado' : 'Seleccionar cliente') : 'Seleccionar Clientes'}</span>
        <span style={{ 
          fontSize: '12px', 
          color: '#fff',
          fontWeight: '500'
        }}>
          {selectedArray.length} seleccionados
        </span>
      </Button>
      
      {/* Chips de clientes seleccionados */}
      {selectedArray.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selectedArray.map((clienteId) => {
            const cliente = clientes.find(c => c.id === clienteId);
            return (
              <Chip 
                key={clienteId} 
                label={cliente ? renderClienteLabel(cliente) : clienteId} 
                size="small"
                onDelete={() => handleClienteToggle(clienteId, false)}
                sx={{
                  backgroundColor: 'rgb(70, 120, 195)',
                  color: '#fff',
                  fontWeight: 500,
                  '& .MuiChip-deleteIcon': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&:hover': {
                      color: '#fff'
                    }
                  }
                }}
              />
            );
          })}
        </Box>
      )}
      
      <Collapse in={open}>
        <div style={{ marginTop: '12px', padding: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
          {/* Campo de búsqueda */}
          <TextField
            fullWidth
            placeholder="Buscar clientes por nombre..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    edge="end"
                    sx={{
                      color: 'rgba(0, 0, 0, 0.54)',
                      backgroundColor: 'transparent',
                      borderRadius: '50%',
                      width: 28,
                      height: 28,
                      padding: 0,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        color: 'rgba(0, 0, 0, 0.87)'
                      }
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            size="small"
            style={{ marginBottom: '12px' }}
            sx={{
              '& .MuiInputBase-input::placeholder': {
                color: '#666',
                opacity: 1,
                fontSize: '14px'
              },
              '& .MuiInputBase-root': {
                backgroundColor: '#fff',
                borderRadius: '4px'
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#ddd'
                },
                '&:hover fieldset': {
                  borderColor: 'rgb(70, 120, 195)'
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgb(70, 120, 195)'
                }
              }
            }}
          />
          
          {/* Información de resultados */}
          <div style={{ 
            marginBottom: '12px', 
            padding: '8px', 
            backgroundColor: '#f5f5f5', 
            borderRadius: '4px',
            fontSize: '12px',
            color: '#666'
          }}>
            {searchTerm 
              ? `${filteredClientes.length} clientes encontrados • ${selectedArray.length} seleccionados`
              : `${clientes.length} clientes disponibles • ${selectedArray.length} seleccionados`
            }
          </div>
          
          {/* Lista de clientes */}
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {filteredClientes.length > 0 ? (
              <>
                {filteredClientes.map((cliente) => {
                  const isSelected = selectedArray.includes(cliente.id);
                  console.log('🔍 Renderizando cliente:', { id: cliente.id, nombre: cliente.nombre, isSelected, selectedArray });
                  return (
                    <div key={cliente.id} style={{ 
                      padding: '8px', 
                      borderBottom: '1px solid #eee',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      backgroundColor: isSelected ? 'rgba(70, 120, 195, 0.08)' : 'transparent',
                      borderLeft: isSelected ? '3px solid rgb(70, 120, 195)' : 'none'
                    }}>
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => handleClienteToggle(cliente.id, e.target.checked)}
                        size="small"
                        disabled={disabled}
                        sx={{
                          color: 'rgb(70, 120, 195)',
                          '&.Mui-checked': {
                            color: 'rgb(70, 120, 195)'
                          }
                        }}
                      />
                      <span style={{ 
                        flex: 1,
                        fontSize: '13px',
                        fontWeight: isSelected ? '500' : 'normal',
                        color: isSelected ? 'rgb(70, 120, 195)' : 'inherit'
                      }}>
                        {renderClienteLabel(cliente)}
                      </span>
                    </div>
                  );
                })}
                
                {/* Loading indicator para más datos */}
                {isLoadingMore && (
                  <div style={{ textAlign: 'center', padding: '12px' }}>
                    <CircularProgress size={20} />
                    <Typography variant="caption" display="block">
                      Cargando más clientes...
                    </Typography>
                  </div>
                )}
                
                {/* Observer target para infinite scroll */}
                {hasMore && !isLoadingMore && (
                  <div ref={observerRef} style={{ height: '20px' }} />
                )}
                
                {/* Mensaje de fin */}
                {!hasMore && filteredClientes.length > 0 && (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '12px', 
                    color: '#666',
                    fontSize: '12px',
                    borderTop: '1px solid #eee',
                    marginTop: '8px'
                  }}>
                    No hay más clientes que mostrar
                  </div>
                )}
              </>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px', 
                color: '#666',
                fontSize: '14px'
              }}>
                {searchTerm 
                  ? `No se encontraron clientes que coincidan con "${searchTerm}"`
                  : "Busca clientes para ver los disponibles"
                }
              </div>
            )}
          </div>
        </div>
      </Collapse>
    </FormControl>
  );
};

export default ClienteSearch;